import type { CounterpartyTinReview } from "@common/schemas/counterpartyTin.schema";
import type { CounterpartyTinIds } from "@api/counterpartyTin.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { useUser } from "@hooks/useUser";
import { useTinReviewActions } from "./useTinReviewActions";
import styles from "./CounterpartyTinSection.module.scss";

interface TinReviewStatusProps {
    ids: CounterpartyTinIds;
    tinRevisionId: string;
    reviews: CounterpartyTinReview[];
}

function getReviewMessage({
    isReviewed,
    reviewerNames,
}: {
    isReviewed: boolean;
    reviewerNames: string[];
}) {
    if (!isReviewed) {
        return "Not reviewed";
    }
    return reviewerNames.length > 0
        ? `Reviewed by ${reviewerNames.join(", ")}`
        : "Reviewed";
}

export const TinReviewStatus = ({
    ids,
    tinRevisionId,
    reviews,
}: TinReviewStatusProps) => {
    const currentUser = useUser();
    const { data: members } = useCurrentWorkspaceMembers();
    const { markReviewed, undoReview, isPending } = useTinReviewActions({
        ids,
        tinRevisionId,
    });
    const isReviewedByMe = reviews.some(
        (review) => review.userId === currentUser.id,
    );
    const reviewerNames = members
        ? reviews.map(
              (review) =>
                  members.find((member) => member.user.id === review.userId)
                      ?.user.name ?? "a former member",
          )
        : [];

    return (
        <HStack gap={8} align="center" justify="between" wrap>
            <span className={styles.message}>
                {getReviewMessage({
                    isReviewed: reviews.length > 0,
                    reviewerNames,
                })}
            </span>
            <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={isPending}
                onClick={isReviewedByMe ? undoReview : markReviewed}
            >
                {isReviewedByMe ? "Undo review" : "Mark reviewed"}
            </Button>
        </HStack>
    );
};
